import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import './ProductImport.css';
import { useImageLoader } from '../../components/product-page/ProductGroupDetailCommon';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ProductImport = () => {
  const [excelFile, setExcelFile] = useState(null);
  const [imageFiles, setImageFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const [pendingImports, setPendingImports] = useState([]);
  const [selectedImport, setSelectedImport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [heroFile, setHeroFile] = useState(null);
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroMessage, setHeroMessage] = useState('');

  const getImageUrl = useImageLoader();
  const excelInputRef = useRef(null);
  const imagesInputRef = useRef(null);

  const fetchPending = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/product-imports/admin/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const parsed = data.data.map(p => ({
        ...p,
          variants: typeof p.variants === 'string'? JSON.parse(p.variants) : p.variants,
        }));
        setPendingImports(parsed);
      }
    } catch (err) {
      console.error('Fetch pending imports error', err);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  // ------------------ VALIDATE EXCEL + IMAGES ------------------
  const validateBeforeUpload = async () => {
    if (!excelFile) {
      setValidationErrors([{ row: 0, productName: '-', reason: 'Please select products.xlsx' }]);
      return false;
    }
    if (!imageFiles || imageFiles.length === 0) {
      setValidationErrors([{ row: 0, productName: '-', reason: 'Please select images folder' }]);
      return false;
    }

    setValidating(true);
    setValidationErrors([]);
    setResult(null);

    try {
      const data = await excelFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

      if (rows.length === 0) {
        setValidationErrors([{ row: 0, productName: '-', reason: 'Excel file is empty' }]);
        setValidating(false);
        return false;
      }

      const uploadedImageSet = new Set(
        Array.from(imageFiles).map(f => f.name.toLowerCase().trim())
      );

      const errors = [];
      rows.forEach((row, idx) => {
        const rowNum = idx + 2;
        const productName = row['Product Name'] || row['product_name'] || `Row ${rowNum}`;
        const imgName = row['Image'] || row['image_filename'] || row['image'] || row['variant_image'];

        if (!imgName) {
          errors.push({
            row: rowNum,
            productName,
            reason: 'Missing Image. Add value to Image column in Excel'
          });
          return;
        }

        const imgNameClean = String(imgName).trim().toLowerCase();
        if (!uploadedImageSet.has(imgNameClean)) {
          errors.push({
            row: rowNum,
            productName,
            reason: `Image '${imgName}' not found in selected folder`
          });
        }
      });

      if (errors.length > 0) {
        setValidationErrors(errors);
        setValidating(false);
        return false;
      }

      setValidating(false);
      return true;
    } catch (err) {
      console.error('Validate error', err);
      setValidationErrors([{ row: 0, productName: '-', reason: 'Cannot read Excel file. Check format' }]);
      setValidating(false);
      return false;
    }
  };

  // ------------------ Upload Folder ------------------
  const handleUploadFolder = async () => {
    const isValid = await validateBeforeUpload();
    if (!isValid) return;

    setUploading(true);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('excel', excelFile);

      Array.from(imageFiles).forEach(file => {
        formData.append('images', file);
      });

      const res = await fetch(`${API_URL}/product-imports/admin/upload-folder`, {
        method: 'POST',
        headers: token? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      const data = await res.json();
      if (data.success) {
        setResult(`✅ Import successful! ${data.data.imported} products added to pending`);
        setExcelFile(null);
        setImageFiles(null);
        setValidationErrors([]);
        if (excelInputRef.current) excelInputRef.current.value = '';
        if (imagesInputRef.current) imagesInputRef.current.value = '';
        fetchPending();
      } else {
        setResult(`❌ Import failed: ${data.error}`);
      }
    } catch (err) {
      console.error(err);
      setResult('❌ Network error');
    } finally {
      setUploading(false);
    }
  };

  // ------------------ Upload Hero Image ------------------
  const handleUploadHero = async () => {
    if (!selectedImport ||!heroFile) return;
    setHeroUploading(true);
    setHeroMessage('');

    const formData = new FormData();
    formData.append('hero_image', heroFile);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/product-imports/admin/${selectedImport.id}/upload-hero-image`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      const data = await res.json();
      if (data.success) {
        setHeroMessage('✅ Hero image uploaded successfully');
        setSelectedImport(prev => ({
        ...prev,
          hero_image: data.imageUrl,
        }));
      } else {
        setHeroMessage(`❌ ${data.error}`);
      }
    } catch (err) {
      console.error(err);
      setHeroMessage('❌ Upload failed (network)');
    } finally {
      setHeroUploading(false);
    }
  };

  // ------------------ Finalize ------------------
  const handleFinalize = async () => {
    if (!selectedImport) return;
    if (!selectedImport.variants?.every(v => v.image_url)) {
      return alert('Some variant images are missing!');
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/product-imports/admin/${selectedImport.id}/finalize`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      if (data.success) {
        alert('✅ Finalized!');
        closeModal();
        fetchPending();
      } else {
        alert(`❌ ${data.error}`);
      }
    } catch (err) {
      console.error(err);
      alert('❌ Finalize failed');
    }
  };

  const handleRemoveAllPending = async () => {
    const confirmDelete = window.confirm(
      'Are you sure you want to remove ALL pending imports? This cannot be undone.'
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/product-imports/admin/removeAllPending`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) fetchPending();
      else alert(`❌ ${data.error}`);
    } catch (err) {
      console.error(err);
      alert('❌ Failed to remove pending imports');
    }
  };

  const closeModal = () => {
    setSelectedImport(null);
    setIsModalOpen(false);
    setHeroFile(null);
    setHeroMessage('');
  };

  const resetUploadForm = () => {
    setExcelFile(null);
    setImageFiles(null);
    setValidationErrors([]);
    setResult(null);
    if (excelInputRef.current) excelInputRef.current.value = '';
    if (imagesInputRef.current) imagesInputRef.current.value = '';
  };

  return (
    <div className="productImportPage">
      <h2>Import Products</h2>

      {/* Upload Folder */}
      <div className="uploadSection">
        <h4>1. Select Excel file</h4>
        <input
          ref={excelInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={e => {
            setExcelFile(e.target.files?.[0] || null);
            setValidationErrors([]);
            setResult(null);
          }}
        />
        {excelFile && <p>Selected: {excelFile.name}</p>}

        <h4>2. Select images folder</h4>
        <input
          ref={imagesInputRef}
          type="file"
          // @ts-ignore
          webkitdirectory=""
          directory=""
          multiple
          onChange={e => {
            setImageFiles(e.target.files);
            setValidationErrors([]);
            setResult(null);
          }}
        />
        {imageFiles && <p>Selected: {imageFiles.length} images</p>}

        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button
            onClick={handleUploadFolder}
            disabled={uploading || validating}
          >
            {validating? 'Validating...' : uploading? 'Uploading...' : 'Upload Folder'}
          </button>
          <button onClick={resetUploadForm} disabled={uploading || validating}>
            Clear
          </button>
        </div>

        {result && <p style={{ marginTop: 12 }}>{result}</p>}

        {validationErrors.length > 0 && (
          <div className="errorTable" style={{ marginTop: 20 }}>
            <h4 style={{ color: '#d32f2f' }}>
              ❌ Validation failed. Fix {validationErrors.length} errors:
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <thead>
                <tr style={{ background: '#ffebee' }}>
                  <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Row</th>
                  <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Product Name</th>
                  <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {validationErrors.map((err, i) => (
                  <tr key={i}>
                    <td style={{ border: '1px solid #ccc', padding: 8 }}>{err.row}</td>
                    <td style={{ border: '1px solid #ccc', padding: 8 }}>{err.productName}</td>
                    <td style={{ border: '1px solid #ccc', padding: 8, color: '#d32f2f' }}>
                      {err.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <hr />

      <h3>Pending Imports</h3>
      <section className="header-actions">
        <button onClick={handleRemoveAllPending} className="btn-delete">
          Remove All Pending Imports
        </button>
      </section>
      <ul>
        {pendingImports.map(p => (
          <li key={p.id}>
            <strong>{p.group_title}</strong> ({p.brand}) - Status: {p.status}
            <button
              className="btn-edit"
              onClick={() => {
                setSelectedImport(p);
                setIsModalOpen(true);
              }}
            >
              Edit
            </button>
          </li>
        ))}
      </ul>

      {/* MODAL */}
      {isModalOpen && selectedImport && (
        <div className="modalOverlay" onClick={closeModal}>
          <div className="modalContent" onClick={e => e.stopPropagation()}>
            <div className="modalClose" onClick={closeModal}>✕</div>
            <h3>{selectedImport.group_title}</h3>

            {/* Hero Image */}
            <div style={{ marginBottom: 20 }}>
              <h4>Hero Image</h4>
              <input
                type="file"
                accept="image/*"
                onChange={e => setHeroFile(e.target.files?.[0] || null)}
              />
              {selectedImport.hero_image && (
                <img src={getImageUrl(selectedImport.hero_image)} width={100} alt="Hero" />
              )}
              <button onClick={handleUploadHero} style={{ marginTop: '10px' }}>
                {heroUploading? 'Uploading...' : 'Upload Hero'}
              </button>
              {heroMessage && <p>{heroMessage}</p>}
            </div>

            {/* Variant Table */}
            <h4>Variants</h4>
            <table cellPadding={8} style={{ width: '100%', marginBottom: 20 }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Attribute</th>
                  <th>Size</th>
                  <th>Stock</th>
                  <th>Image</th>
                </tr>
              </thead>
              <tbody>
                {selectedImport.variants.map((v, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{v.attribute || 'N/A'}</td>
                    <td>{v.size || 'N/A'}</td>
                    <td>{v.inventory_qty}</td>
                    <td>
                      {v.image_url? (
                        <img src={getImageUrl(v.image_url)} width={60} alt="" />
                      ) : (
                        <span style={{ color: 'red' }}>Missing</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <button onClick={handleFinalize}>Finalize Import</button>
            <button onClick={closeModal} style={{ marginLeft: 10 }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductImport;